import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { Field, NumberInput } from 'ui';
import { displayPrice } from 'utils';
import ErrorMessage from '~/components/Form/ErrorMessage';
import ListFlowExpandablePanel from '~/components/ListFlow/Elements/ListFlowExpandablePanel';
import RequiredStar from '~/components/ListFlow/RequiredStar';
import SidelineProPaywallModal from '~/components/SidelinePro/Paywall/PaywallModal';
import { SidelineProBadge } from '~/components/SidelinePro/ProBadges';
import useHasFeature from '~/hooks/useHasFeature';
import { useIsSidelineProAvailable } from '~/hooks/useIsSidelinePro';
import {
  changedMinimumOfferThreshold,
  getListFlowMinimumOffer,
  getListFlowPricing,
} from '~/store/listFlow/slices/pricing';
import { getListFlowMinimumOfferError } from '~/store/listFlow/ui';
import { useAppDispatch, useAppSelector } from '~/store/store';

export default function ListFlowMinimumOffer() {
  const dispatch = useAppDispatch();
  const itemPrice = useAppSelector(getListFlowPricing)?.listPrice;
  const minimumOffer = useAppSelector(getListFlowMinimumOffer);
  const error = useAppSelector(getListFlowMinimumOfferError);
  const sidelineProAvailable = useIsSidelineProAvailable();
  const hasFeature = useHasFeature('minimum_offer_threshold');
  const [paywallOpen, setPaywallOpen] = useState(false);

  useEffect(() => {
    if (sidelineProAvailable) {
      // TODO: Set the "default" value within the action based on the Rails setting
      // dispatch(changedMinimumOfferThreshold({ amount: userConfiguration?.minimum_offer_threshold }));
    }
  }, [sidelineProAvailable]);

  if (!sidelineProAvailable) {
    return null;
  }

  return (
    <>
      <ListFlowExpandablePanel
        expanded={minimumOffer.enabled}
        setExpanded={expanded => {
          if (hasFeature) {
            dispatch(changedMinimumOfferThreshold({ enabled: expanded }));
          } else {
            setPaywallOpen(true);
          }
        }}
        title={
          <span>
            Auto-Reject Low Offers <SidelineProBadge />
          </span>
        }
      >
        <div className="mb-4 text-sm">
          Automatically decline an offer if it is below your stated threshold.
        </div>

        <Field
          label={
            <span>
              Minimum Threshold <RequiredStar />
            </span>
          }
          hint={
            minimumOffer.amount && itemPrice ? (
              <span className="text-turf-green-500 text-sm">
                We will not accept offers for your item that are below{' '}
                {displayPrice(
                  itemPrice - itemPrice * (minimumOffer.amount / 100),
                )}
                .
              </span>
            ) : null
          }
        >
          <NumberInput
            error={error}
            rightAdornment={
              <span
                className={clsx(
                  'bg-off-white-500 flex h-full items-center justify-center px-3.5',
                  'rounded-r border-l',
                )}
              >
                %
              </span>
            }
            thousandSeparator
            decimalScale={0}
            onValueChange={values =>
              dispatch(
                changedMinimumOfferThreshold({ amount: values.floatValue }),
              )
            }
            value={minimumOffer.amount ?? ''}
          />
        </Field>

        <ErrorMessage errors={error} />
      </ListFlowExpandablePanel>

      {!hasFeature && (
        <SidelineProPaywallModal
          open={paywallOpen}
          onClose={() => setPaywallOpen(false)}
          source="Auto-reject"
        />
      )}
    </>
  );
}
